/**
 * ¿Esta persona puede CAMBIAR cosas, o solo mirar?
 *
 * 🚨🚨 Salió de la auditoría del 21-ago-2026 y era lo más grave de la consola:
 * alguien con rol «Solo consulta» **podía aprobar entregas**. La pantalla se lo
 * decía —«puede mirar, no puede cambiar nada»— pero nadie lo verificaba.
 *
 * ⛔ Esto es solo para no mostrarle botones que no va a poder usar. **La
 * decisión de verdad la toma el servidor**, que rechaza cualquier escritura de
 * una cuenta de consulta. Esconder el botón no es seguridad: es cortesía.
 *
 * Quien entra con la clave compartida no tiene rol y puede todo: es la puerta
 * de emergencia mientras la oficina termina de pasarse a sus correos.
 */
export function puedeCambiar(): boolean {
  return quienEntro()?.rol !== 'CONSULTA'
}

/**
 * La puerta de la consola.
 *
 * 🚨 Antes no había puerta: cualquiera que supiera el dominio veía los viajes,
 * los clientes y las cédulas de los conductores. Ahora pide clave — y la piden
 * también las rutas de datos, porque una clave solo en la pantalla no sirve de
 * nada si los datos se pueden pedir directo al servidor.
 */

import { useState } from 'react'

const LLAVE = 'symtrack.oficina'

export function tokenGuardado(): string | null {
  return localStorage.getItem(LLAVE)
}

export function salirDeLaConsola(): void {
  localStorage.removeItem(LLAVE)
  location.reload()
}

/** Añade la sesión de oficina a cualquier petición de la consola. */
export async function pedirConSesion<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const token = tokenGuardado()
  const r = await fetch(ruta, {
    ...opciones,
    headers: {
      ...(opciones.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opciones.headers,
    },
  })

  // La sesión venció: sacar a la puerta en vez de dejar la pantalla en blanco.
  if (r.status === 401) {
    localStorage.removeItem(LLAVE)
    location.reload()
    throw new Error('Sesión vencida')
  }

  /*
   * 🚨 El mensaje del servidor tiene que LLEGAR a la pantalla.
   *
   * Medido: el servidor respondía «Este archivo no es un PDF de verdad, vuelve
   * a escanearlo» y la consola mostraba «/api/admin/… respondió 400». Quien
   * está en la oficina no puede hacer nada con eso, y el trabajo de escribir
   * mensajes claros en el servidor se botaba entero — en TODAS las pantallas.
   *
   * El número de la ruta solo se usa cuando el servidor no dijo nada.
   */
  if (!r.ok) {
    const cuerpo = (await r.json().catch(() => null)) as { error?: string } | null
    throw new Error(cuerpo?.error || `${ruta} respondió ${r.status}`)
  }
  return r.json() as Promise<T>
}

/** Lo que sabemos de quien entró, para saludarlo y para saber qué puede hacer. */
export interface Quien {
  id: string
  nombre: string | null
  correo: string
  rol: 'ADMIN' | 'LOGISTICO' | 'CONSULTA'
}

const LLAVE_QUIEN = 'symtrack_consola_quien'

export function quienEntro(): Quien | null {
  try {
    const crudo = localStorage.getItem(LLAVE_QUIEN)
    return crudo ? (JSON.parse(crudo) as Quien) : null
  } catch {
    return null
  }
}

/**
 * La puerta de la consola.
 *
 * 🔑 Se entra con **el correo de la empresa y su misma clave**. Lo pidió Luis:
 *
 *   «Yo autorizo al correo del usuario, y él con su contraseña del correo
 *    ingresa. Así no tenemos que estar creando cuentas nuevas ni que se le
 *    olvide la contraseña a la persona.»
 *
 * ⛔ Esa clave **no se guarda aquí**. Se le pregunta al servidor de correo si es
 * la correcta, y se bota. En este sistema no queda nada que robar.
 *
 * ⛔ La clave compartida de siempre sigue funcionando, abajo y discreta: quitarla
 * el mismo día dejaría a la oficina por fuera. Pero quien entra por ella no
 * queda identificado, y por eso no es la que se ofrece de primeras.
 */
export function Entrar({ alEntrar }: { alEntrar: () => void }) {
  const [correo, setCorreo] = useState('')
  const [claveCorreo, setClaveCorreo] = useState('')
  const [clave, setClave] = useState('')
  const [conLaVieja, setConLaVieja] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)

  async function entrarConCorreo(e: React.FormEvent) {
    e.preventDefault()
    if (entrando || !correo || !claveCorreo) return
    setEntrando(true)
    setError(null)
    try {
      const r = await fetch('/api/admin/interno/entrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo, clave: claveCorreo }),
      })
      const datos = (await r.json()) as { token?: string; quien?: Quien; error?: string }
      if (!r.ok || !datos.token) throw new Error(datos.error ?? 'No se pudo entrar')
      localStorage.setItem(LLAVE, datos.token)
      if (datos.quien) localStorage.setItem(LLAVE_QUIEN, JSON.stringify(datos.quien))
      alEntrar()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo entrar')
      setEntrando(false)
    }
  }

  async function entrarConLaVieja(e: React.FormEvent) {
    e.preventDefault()
    if (entrando || !clave) return
    setEntrando(true)
    setError(null)
    try {
      const r = await fetch('/api/admin/entrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clave }),
      })
      const datos = (await r.json()) as { token?: string; error?: string }
      if (!r.ok || !datos.token) throw new Error(datos.error ?? 'No se pudo entrar')
      localStorage.setItem(LLAVE, datos.token)
      localStorage.removeItem(LLAVE_QUIEN)
      alEntrar()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo entrar')
      setEntrando(false)
    }
  }

  return (
    <div className="entrar-fondo">
      {/* 🚨 La cabecera con el LOGO DE VERDAD. Estaba en el proyecto desde el
          principio —la barra de la consola ya lo servía— y aquí había un
          cuadrito con letras. Misma estructura de gestion.gruposym.co. */}
      <header className="entrar-cabecera">
        <div className="entrar-cabecera-dentro">
          <img src="/icon-192.png" alt="Grupo S&M" className="entrar-logo" />
          <div>
            <p className="entrar-nombre">GrupoSyM</p>
            <p className="entrar-lema">Operaciones Integrales</p>
          </div>
        </div>
      </header>
      <div className="entrar-franja" />

      <div className="entrar-centro">
      <div className="entrar-caja">

        {!conLaVieja ? (
          <form onSubmit={entrarConCorreo}>
            <p className="entrar-acceso">Acceso seguro</p>
            <h1 className="entrar-titulo">Iniciar sesión</h1>
            <p className="entrar-guia">
              Usa tu correo corporativo y su misma clave para continuar.
            </p>

            <label htmlFor="correo" className="entrar-etiqueta">
              Correo electrónico
            </label>
            <input
              id="correo"
              type="email"
              inputMode="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="nombre@gruposym.co"
              autoComplete="username"
              autoFocus
              className="entrar-campo"
            />

            <label htmlFor="claveCorreo" className="entrar-etiqueta">
              Contraseña
            </label>
            <input
              id="claveCorreo"
              type="password"
              value={claveCorreo}
              onChange={(e) => setClaveCorreo(e.target.value)}
              autoComplete="current-password"
              className="entrar-campo"
            />

            {error && <p className="entrar-error">{error}</p>}

            <button
              type="submit"
              disabled={entrando || !correo || !claveCorreo}
              className="entrar-boton"
            >
              {entrando ? 'Comprobando…' : 'Entrar al sistema'}
            </button>

            {/* ⛔ No es una advertencia: es lo que la gente necesita saber para
                confiar en escribir ahí la clave de su correo. */}
            <p className="entrar-nota">
              Tu clave no se guarda aquí. Se le pregunta al servidor de correo y
              se descarta.
            </p>

            <button type="button" onClick={() => setConLaVieja(true)} className="entrar-otra">
              Entrar con la clave de la oficina
            </button>
          </form>
        ) : (
          <form onSubmit={entrarConLaVieja}>
            <p className="entrar-acceso">Acceso de la oficina</p>
            <h1 className="entrar-titulo">Iniciar sesión</h1>
            <p className="entrar-guia">La clave compartida de siempre.</p>

            <label htmlFor="clave" className="entrar-etiqueta">
              Clave de la oficina
            </label>
            <input
              id="clave"
              type="password"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              autoComplete="current-password"
              autoFocus
              className="entrar-campo"
            />

            {error && <p className="entrar-error">{error}</p>}

            <button type="submit" disabled={entrando || !clave} className="entrar-boton">
              {entrando ? 'Entrando…' : 'Entrar al sistema'}
            </button>

            <p className="entrar-nota">
              Entrando así no queda tu nombre en lo que apruebes o devuelvas.
            </p>

            <button type="button" onClick={() => setConLaVieja(false)} className="entrar-otra">
              Entrar con mi correo
            </button>
          </form>
        )}

        {/* El pie de la casa: el NIT y las cuatro barritas de la marca. */}
        <div className="entrar-pie">
          <span className="entrar-nit">NIT 900176548-4</span>
          <div className="entrar-barritas">
            <span style={{ background: '#1a6fba' }} />
            <span style={{ background: '#1890cc' }} />
            <span style={{ background: '#5fb847' }} />
            <span style={{ background: '#a8c00c' }} />
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
